import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Star, Sparkles, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface WriteReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type MeReview = {
  review: {
    id: string;
    rating: number;
    body: string;
    isAnonymous: boolean;
    status: string;
  } | null;
  profile: {
    displayName: string | null;
    profileImageUrl: string | null;
  };
};

export default function WriteReviewModal({ open, onOpenChange }: WriteReviewModalProps) {
  const { t, language } = useTranslation();
  const { toast } = useToast();

  const { data: me, isLoading: meLoading } = useQuery<MeReview>({
    queryKey: ['/api/reviews/me'],
    enabled: open,
  });

  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const displayName = me?.profile?.displayName ?? "";
  const profileImageUrl = me?.profile?.profileImageUrl ?? null;
  const isUpdate = !!me?.review;

  // Hydrate form from server data once per open. Reset on close.
  useEffect(() => {
    if (!open) {
      setHydrated(false);
      setRating(0);
      setHoverRating(0);
      setBody("");
      setAnonymous(false);
      setSubmitting(false);
      return;
    }
    if (!hydrated && me) {
      if (me.review) {
        setRating(me.review.rating);
        setBody(me.review.body);
        setAnonymous(me.review.isAnonymous);
      }
      setHydrated(true);
    }
  }, [open, me, hydrated]);

  const closeLabel = t('reviewModal.close');

  const onSubmit = async () => {
    if (rating < 1) {
      toast({ title: t('reviewModal.validationRating'), variant: "destructive" });
      return;
    }
    if (body.trim().length < 10) {
      toast({ title: t('reviewModal.validationBody'), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest("POST", "/api/reviews", {
        rating,
        body: body.trim(),
        isAnonymous: anonymous,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/reviews'] });
      queryClient.invalidateQueries({ queryKey: ['/api/reviews/me'] });
      toast({
        title: t('reviewModal.successTitle'),
        description: t('reviewModal.successDesc'),
      });
      onOpenChange(false);
    } catch {
      toast({
        title: t('reviewModal.errorTitle'),
        description: t('reviewModal.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && createPortal(
        <>
          <img
            src="/gratimy-bubble-blue.webp"
            alt=""
            aria-hidden
            width={256}
            height={256}
            decoding="async"
            className="gratimy-floating-bubble gratimy-floating-bubble--tr"
          />
          <img
            src="/gratimy-bubble-blush.webp"
            alt=""
            aria-hidden
            width={256}
            height={256}
            decoding="async"
            className="gratimy-floating-bubble gratimy-floating-bubble--bl"
          />
        </>,
        document.body,
      )}
      <DialogContent
        hideClose
        className="goal-form-dialog goal-form-dialog--scroll-hidden sm:max-w-[640px] max-h-[90vh] overflow-y-auto p-8"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={closeLabel}
          className="absolute right-4 top-4 z-50 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all duration-200"
          data-testid="button-close-write-review"
        >
          <X className="h-4 w-4" />
        </button>

        <DialogHeader className="pr-12 space-y-2">
          <DialogTitle asChild>
            <h2
              className="text-3xl text-white"
              style={{ fontFamily: "'ZT Gatha', serif", fontWeight: 600 }}
            >
              {t('reviewModal.title')}
            </h2>
          </DialogTitle>
          <DialogDescription className="text-white/85 text-sm leading-relaxed">
            {t('reviewModal.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {meLoading && !hydrated ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        ) : (
          <div className="space-y-5 py-6">
            {/* Rating */}
            <div>
              <Label className="block text-sm font-semibold text-white mb-2">
                {t('reviewModal.ratingLabel')}
              </Label>
              <div className="flex items-center gap-2" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = n <= (hoverRating || rating);
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      aria-label={t('reviewModal.starAria', { count: String(n) })}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`button-star-${n}`}
                    >
                      <Star
                        className={`w-8 h-8 ${active ? 'fill-[#FFE45F] text-[#FFE45F]' : 'text-white/70'}`}
                      />
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-white/70 mt-1">{t('reviewModal.ratingHint')}</p>
            </div>

            {/* Body */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <Label htmlFor="review-body" className="block text-sm font-semibold text-white">
                  {t('reviewModal.bodyLabel')}
                </Label>
                <span
                  className="text-xs font-medium tabular-nums text-white/70"
                  data-testid="review-body-counter"
                >
                  {body.length}/2000
                </span>
              </div>
              <Textarea
                id="review-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={t('reviewModal.bodyPlaceholder')}
                maxLength={2000}
                className="w-full pl-4 pr-4 pt-3 min-h-[140px]"
                data-testid="textarea-review-body"
              />
            </div>

            {/* Identity toggle */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-white/15 border border-white/25">
              <div className="flex items-center gap-3 min-w-0">
                {!anonymous && profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border border-white/40 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {anonymous ? '?' : (displayName.charAt(0).toUpperCase() || 'G')}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {anonymous
                      ? t('reviewModal.postAnonymously')
                      : t('reviewModal.postAs', { name: displayName || (language === 'da' ? 'dig' : 'you') })}
                  </div>
                </div>
              </div>
              <Switch
                checked={anonymous}
                onCheckedChange={setAnonymous}
                data-testid="switch-anonymous"
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-center sm:gap-3 pt-2">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="button-cancel-review"
            className="w-full sm:w-auto h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/30 text-white px-8 shadow-none"
          >
            {t('reviewModal.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitting || meLoading}
            data-testid="button-submit-review"
            className="w-full sm:w-auto h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white border-0 px-8 shadow-[0_10px_24px_rgba(59,130,246,0.35)]"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {submitting
              ? t('reviewModal.submitting')
              : isUpdate
              ? t('reviewModal.update')
              : t('reviewModal.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
